import { cn } from "@faire/prototype-playground/lib/utils";
import { ChevronLeft } from "@faire/slate-icons/ChevronLeft";
import { ChevronRight } from "@faire/slate-icons/ChevronRight";
import { Button } from "@faire/slate/components/button";
import { IconButton } from "@faire/slate/components/icon-button";
import React from "react";

import { SECTION_TITLE_TYPE } from "./constants";

interface SectionHeaderProps {
  title: string;
  showArrows?: boolean;
  /**
   * Drops the paging arrows below `md`. For rails that let the next card peek
   * on mobile: the peek and the swipe are the affordance there, and two 32px
   * targets in the header are furniture for a gesture the reader already has.
   */
  arrowsDesktopOnly?: boolean;
  /** Hide the "See all" link — e.g. a single-item feature row has nothing more. */
  showSeeAll?: boolean;
  seeAllLabel?: string;
  /** "link" = underlined text (default); "button" = a bordered pill CTA. */
  seeAllVariant?: "link" | "button";
  onSeeAll?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  showArrows = true,
  arrowsDesktopOnly = false,
  showSeeAll = true,
  seeAllLabel = "See all",
  seeAllVariant = "link",
  onSeeAll,
  onPrev,
  onNext,
}) => {
  const seeAll =
    seeAllVariant === "button" ? (
      <Button variant="secondary" size="small" onClick={onSeeAll}>
        {seeAllLabel}
      </Button>
    ) : (
      <button
        type="button"
        onClick={onSeeAll}
        className="type-fs-paragraph text-fs-text-primary underline"
      >
        {seeAllLabel}
      </button>
    );
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className={cn(SECTION_TITLE_TYPE, "text-fs-text-primary")}>
        {title}
      </h2>
      <div className="flex items-center gap-2">
        {showSeeAll ? seeAll : null}
        {showArrows ? (
          <div
            className={cn(
              "flex items-center gap-2",
              arrowsDesktopOnly ? "hidden md:flex" : undefined
            )}
          >
            <IconButton
              Icon={ChevronLeft}
              variant="secondary"
              shape="circle"
              size="small"
              aria-label="Previous"
              onClick={onPrev}
            />
            <IconButton
              Icon={ChevronRight}
              variant="secondary"
              shape="circle"
              size="small"
              aria-label="Next"
              onClick={onNext}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
